import {
  AssessmentQuestion,
  CategorisationTreeOption,
  CategorisationTreeQuestion,
} from '../types'
import { AnswerMap } from '../util/categorisationTree'
import { QuestionType } from '../enums'
import ZRadio from './ZRadio'
import ZTextField from './ZTextField'
import ZText from './ZText'
import { SPACE } from '../styles/layout'

type AnyQuestion = CategorisationTreeQuestion | AssessmentQuestion

const TreeQuestion = ({
  question,
  answers,
  onChange,
  tag,
}: {
  question: AnyQuestion
  answers: AnswerMap
  onChange: (
    questionId: string,
    answer: { answerId?: string; answerInput?: string },
  ) => void
  tag?: string
}) => {
  if (question.type === QuestionType.InputNumeric) {
    return (
      <div>
        <ZText style={{ marginBottom: SPACE.sm }}>{question.label}</ZText>

        <ZTextField
          label="Insert value"
          format={question.inputFormat ?? undefined}
          fullWidth
          onChange={input => onChange(question.id, { answerInput: input })}
        />
      </div>
    )
  }

  if (question.type === QuestionType.InputText) {
    return (
      <div>
        <ZText style={{ marginBottom: SPACE.sm }}>{question.label}</ZText>

        <ZTextField
          label="Insert the name of the firm"
          format={question.inputFormat ?? undefined}
          fullWidth
          onChange={input => onChange(question.id, { answerInput: input })}
        />
      </div>
    )
  }

  const options = (question.options ?? []) as CategorisationTreeOption[]

  const isAnswered = answers[question.id]?.answerId != null

  const questionChildren: CategorisationTreeQuestion[] =
    'questionChildren' in question ? (question.questionChildren ?? []) : []

  return (
    <div style={{ display: 'flex', rowGap: SPACE.xl, flexDirection: 'column' }}>
      <div>
        {/* The question sits close to its own answers (0.75rem); the larger
            gap belongs between question groups, set by the parent. */}
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'baseline',
            marginBottom: SPACE.lg,
          }}
        >
          {tag ? <ZText style={{ fontWeight: 700 }}>{tag}</ZText> : null}
          <ZText>{question.label}</ZText>
        </div>

        <ZRadio
          style={{ rowGap: SPACE.xl }}
          options={options.map(option => ({
            value: option.id,
            label: option.label,
            subLabel: option.subLabel ?? undefined,
            checked: option.id === answers[question.id]?.answerId,
          }))}
          onChange={event =>
            onChange(question.id, { answerId: event.target.value })
          }
          renderAfterOption={optionId => {
            const isSelected = optionId === answers[question.id]?.answerId
            if (!isSelected) return null
            const nextQuestions =
              options.find(option => option.id === optionId)?.nextQuestions ??
              []
            return nextQuestions.map(next => (
              <div key={next.id} style={{ marginTop: SPACE.xl }}>
                <TreeQuestion
                  question={next}
                  answers={answers}
                  onChange={onChange}
                />
              </div>
            ))
          }}
        />
      </div>

      {isAnswered
        ? questionChildren.map(child => (
            <TreeQuestion
              key={child.id}
              question={child}
              answers={answers}
              onChange={onChange}
            />
          ))
        : null}
    </div>
  )
}

export default TreeQuestion
